import React, { useState, useEffect } from 'react';
import store, { applicationActions } from '@apply/adminStore';
import { useSelector } from 'react-redux';
import BackSpace from '@material-ui/icons/Backspace';
import Tooltip from '@material-ui/core/Tooltip';
import { ApplicationApi, ListApi } from '@apply/api';
import { BorrowerReview, PropertiesReview, formatCurrency } from '@apply/ui';
import { applicationInsights } from '../../../ApplicationInsights';
import { FileSelectDialog } from '../../../common/SharedComponents';
import { RefreshEvents } from '../../../common/SharedEvents';
import { handleAdminError, handleAdminSuccess } from '../../../common/SharedFunctions';
import { LoadPanel } from 'devextreme-react/load-panel';
import Tabs, { Item } from 'devextreme-react/tabs';
import { Popup } from 'devextreme-react/popup';
import { ApplyFileStatusType } from '../../common/ApplicationConstants';
import { ModalSummaryHeader } from '../../common/ApplicationComponents';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faDownload } from '@fortawesome/free-solid-svg-icons';
import DataGrid, { Column, Paging, Pager } from 'devextreme-react/data-grid';
import { formatDateTime } from '@apply/ui';
import { ApplicationStatusTypeOptions } from '@apply/constants';

const incompleteModalStyles = {
    buttonMarginRight: { marginRight: '10px' },
    nonEditable: { backgroundColor: '#f7f7f7', padding: '5px 10px' },
    noPaddingLeft: { paddingLeft: '0' },
    leftLabel: { paddingLeft: '0', paddingTop: '8px', paddingRight: '0' },
    iconContainer: { paddingTop: '8px', paddingLeft: '0', paddingRight: '0' },
    statusMsgContainer: {
        paddingTop: '8px',
        paddingLeft: '10px',
        paddingRight: '0',
    },
    iconYes: { color: 'mediumblue', cursor: 'pointer' },
    iconNo: { color: 'silver', cursor: 'not-allowed' },
    iconCancel: { color: 'darkred', cursor: 'pointer' },
    buttonContainer: { marginRight: '20px', textAlign: 'right' },
    blue: { color: 'blue' },
    green: { color: 'green' },
    red: { color: 'red' },
    black: { color: 'black' },
    fileRow: { height: '45px' },
    dataHeader: {
        lineHeight: '36px',
    },
    dataField: {
        backgroundColor: 'rgb(230, 230, 230)',
        border: '1px solid silver',
        padding: '5px',
        marginBottom: '5px',
        height: '35px',
        borderRadius: '5px',
        color: 'gray',
    },
};

const FileList = ({ parentId, files, onFileChange, disabled = false }) => {
    //File Upload
    const fileUploadOptions = {
        isOpen: false,
        maxFiles: 1,
        isReplace: false,
        typeName: '',
    };
    const [fileSelectOptions, set_fileSelectOptions] = useState(fileUploadOptions);

    const _setFileStatus = (e, index) => {
        const file = files[index];
        if (!file) {
            return;
        }

        var statusId = parseInt(e.target.value);
        if (statusId === ApplyFileStatusType.NotApplicable) {
            const options = {
                ownerId: file.id,
                isOpen: true,
                maxFiles: 1,
                isReplace: false,
                fileTypeName: file.fileTypeName,
            };
            set_fileSelectOptions(options);
            return;
        }

        file.statusId = statusId;
        onFileChange && onFileChange(file, parentId);
    };

    const onFileSelectSubmit = (id, filesToUpload) => {
        if (!id) {
            return;
        }

        if (!filesToUpload || filesToUpload.length === 0) {
            _onRemoveTempFile(id);
            set_fileSelectOptions({ ...fileSelectOptions, isOpen: false });
            return;
        }

        const fileToUpdate = files.find((f) => f.id === id);
        if (!fileToUpdate) {
            return;
        }

        fileToUpdate.file = filesToUpload[0];
        fileToUpdate.oldStatusId = fileToUpdate.statusId;
        fileToUpdate.statusId = ApplyFileStatusType.Approved;
        onFileChange && onFileChange(fileToUpdate, parentId);
        set_fileSelectOptions({ ...fileSelectOptions, isOpen: false });
    };

    const _onRemoveTempFile = (id) => {
        if (!id) {
            return;
        }

        const fileToUpdate = files.find((f) => f.id === id);
        if (!fileToUpdate) {
            return;
        }

        fileToUpdate.file = null;
        fileToUpdate.statusId = fileToUpdate.oldStatusId ?? fileToUpdate.statusId;
        onFileChange && onFileChange(fileToUpdate, parentId);
    };

    const _getStatusStyle = (statusId) => {
        switch (statusId) {
            case 0:
                return incompleteModalStyles.black;
            case 1:
                return incompleteModalStyles.blue;
            case 2:
                return incompleteModalStyles.green;
            case 3:
                return incompleteModalStyles.red;
            default:
                return null;
        }
    };

    const onFileSelectCancel = () => {
        set_fileSelectOptions({ ...fileSelectOptions, isOpen: false });
    };

    if (!files || files.length === 0) {
        return <div style={{ paddingBottom: '10px' }}>No Files Uploaded</div>;
    }

    return (
        <>
            {files.map((file, index) => {
                return (
                    <div
                        className='row'
                        key={file.id}
                        style={incompleteModalStyles.fileRow}
                    >
                        <div
                            className='columns small-12 large-2'
                            style={incompleteModalStyles.leftLabel}
                        >
                            {file.fileTypeName}
                        </div>
                        <div className='columns small-12 large-3'>
                            {file.statuses && (
                                <select
                                    value={file.statusId ?? 0}
                                    onChange={(e) => _setFileStatus(e, index)}
                                    required
                                    style={_getStatusStyle(file.statusId)}
                                    disabled={disabled}
                                >
                                    {file.statuses &&
                                        file.statuses.map((fs) => {
                                            if (file.statusId > 0 && fs.id === '0') {
                                                return null;
                                            } else {
                                                return (
                                                    <option
                                                        key={fs.id}
                                                        value={fs.id}
                                                        style={incompleteModalStyles.black}
                                                    >
                                                        {fs.name}
                                                    </option>
                                                );
                                            }
                                        })}
                                </select>
                            )}
                            {file.createdDate && (
                                <div
                                    className='columns small-12 large-4'
                                    style={incompleteModalStyles.leftLabel}
                                >
                                    {new Date(file.createdDate).toLocaleDateString('en-us')}
                                </div>
                            )}
                        </div>
                        <div
                            className='columns small-12 large-1'
                            style={incompleteModalStyles.iconContainer}
                        >
                            <Tooltip title='Download file'>
                                <a
                                    href={file.url}
                                    target='_blank'
                                    rel='noreferrer'
                                >
                                    <FontAwesomeIcon
                                        icon={faDownload}
                                        style={file.url ? incompleteModalStyles.iconYes : incompleteModalStyles.iconNo}
                                    />
                                </a>
                            </Tooltip>
                            &nbsp;
                            {file.file && (
                                <Tooltip title='Cancel File upload/replace'>
                                    <BackSpace
                                        style={incompleteModalStyles.iconCancel}
                                        onClick={() => {
                                            _onRemoveTempFile(file.id);
                                        }}
                                    />
                                </Tooltip>
                            )}
                        </div>
                        <div
                            className='columns small-12 large-4'
                            style={incompleteModalStyles.statusMsgContainer}
                        >
                            {file?.statusChangedByMsg && file.statusChangedByMsg}
                        </div>
                    </div>
                );
            })}
            <FileSelectDialog
                options={fileSelectOptions}
                onSubmit={onFileSelectSubmit}
                onCancel={onFileSelectCancel}
            />
        </>
    );
};

const IncompleteReviewModal = () => {
    const { isOpen, id } = useSelector((state) => state.application?.incompleteReviewModal);
    const { dispatch } = store;
    const refreshEvents = new RefreshEvents();
    const applicationApi = new ApplicationApi.ApplicationApi(applicationInsights);
    const listApi = new ListApi.ListApi(applicationInsights);

    const [tabIndex, set_tabIndex] = useState(0);
    const [propertyTabIndex, set_propertyTabIndex] = useState(-1);
    const [subTabIndex, set_subTabIndex] = useState(0);

    const [application, set_application] = useState(null);
    const [properties, set_properties] = useState([]);
    const [property, set_property] = useState(null);
    const [responsibleId, set_responsibleId] = useState(0);
    const [employees, set_employees] = useState([]);
    const [loadingOpen, set_loadingOpen] = useState(false);
    const [loadingMessage, set_loadingMessage] = useState('Loading...');

    const [auditEntries, set_auditEntries] = useState(null);

    const styles = {
        formStyle: {
            height: '490px',
            overflow: 'auto',
            padding: '10px',
        },
        iFrame: {
            height: '480px',
            width: '100%',
            overflow: 'auto',
        },
    };

    useEffect(() => {
        const loadListsAsync = async () => {
            await _loadBfiEmployees();
        };
        loadListsAsync();
    }, []);

    useEffect(() => {
        if (!id) {
            resetState();
            return;
        }

        const loadAppAsync = async () => {
            set_loadingOpen(true);
            await loadApplication(id);
            await loadAuditHistory(id);
            set_loadingOpen(false);
        };
        loadAppAsync();
    }, [id]);

    useEffect(() => {
        if (!properties) {
            set_propertyTabIndex(-1);
            return;
        }

        if (!(propertyTabIndex >= 0)) {
            const firstActiveIndex = properties.findIndex((p) => !p.disabled);
            set_propertyTabIndex(firstActiveIndex);
        }
    }, [properties]);

    useEffect(() => {
        if (!properties || properties.length === 0) {
            set_property(null);
            return;
        }
        if (propertyTabIndex < 0) {
            return;
        }
        set_property(properties[propertyTabIndex]);
    }, [propertyTabIndex]);

    const resetState = () => {
        set_propertyTabIndex(-1);
        set_tabIndex(0);
        set_subTabIndex(0);
        set_propertyTabIndex(-1);
        set_application(null);
        set_properties([]);
        set_property(null);
        set_responsibleId(null);
    };

    const _handleClose = () => {
        dispatch(
            applicationActions.setIncompleteReviewModal({
                incompleteReviewModal: { isOpen: false },
            })
        );
    };

    const loadAuditHistory = async (id) => {
        try {
            const response = await applicationApi.getAuditHistory(id);
            if (response.statusCode !== 200) {
                return;
            }

            const result = response.result;
            set_auditEntries(result);
        } catch (ex) {
            handleAdminError(ex, 'An unexpected error occurred loading audit history. Please try again.');
            set_loadingOpen(false);
        }
    };

    const _loadBfiEmployees = async () => {
        const data = await listApi.getBfiEmployees();
        set_employees(data);
    };

    const onPropertyFileListChange = (file, parentId) => {
        var property = properties.find((p) => p.id === parentId);
        if (!property) {
            return;
        }
        const targetFile = property.files.find((f) => f.id === file.id);
        if (!targetFile) {
            return;
        }

        property.files = property.files.map((u) => (u.id !== file.id ? u : file));
        var newProperties = properties.map((p) => (p.id !== property.id ? p : property));
        set_properties(newProperties);
    };

    const _submitChanges = async (e) => {
        e.preventDefault();
        e.stopPropagation();

        try {
            set_loadingMessage('Saving');
            set_loadingOpen(true);

            const model = {
                id: application.id,
                pocId: responsibleId,
            };

            const response = await applicationApi.updateIncompleteReview(model);
            if (response.statusCode !== 200) {
                set_loadingOpen(false);
                return;
            }
            refreshEvents.dispatchRefreshEvent();

            handleAdminSuccess('Changes saved successfully!');

            setTimeout(() => {
                _handleClose();
            }, 600);
        } catch (ex) {
            handleAdminError(ex, 'An unexpected error occurred. Please try again.');
        } finally {
            set_loadingOpen(false);
        }
    };

    const loadApplication = async (id) => {
        try {
            const app = (await applicationApi.getIncompleteAppReview(id)).result;
            if (!app) {
                return;
            }

            set_application(app);
            set_responsibleId(app.responsibleId);
            set_properties(app.properties);
        } catch (ex) {
            handleAdminError(ex, 'An unexpected error occurred loading application information. Please try again.');
            set_loadingOpen(false);
        }
    };

    const onTabsSelectionChanged = (args) => {
        if (args.name === 'selectedIndex') {
            let ti = args.value;
            set_tabIndex(ti);
        }
    };

    const onPropertyTabsSelectionChanged = (args) => {
        if (args.name === 'selectedIndex') {
            let ti = args.value;
            if (args.value >= 0) {
                let prop = properties[ti];
                if (prop.disabled) {
                    ti = properties.findIndex((p) => !p.disabled);
                }
            }
            set_propertyTabIndex(ti);
            set_subTabIndex(0);
        }
    };

    const onSubTabsSelectionChanged = (args) => {
        if (args.name === 'selectedIndex') {
            let ti = args.value;
            set_subTabIndex(ti);
        }
    };

    const displayBudgetValues = (formValues, level) => {
        if (!formValues) {
            return null;
        }
        if (typeof formValues === 'object') {
            return Object.keys(formValues).map((v, index) => {
                if (level === 0) {
                    return (
                        <span key={'key' + index}>
                            <h2 className='decorated'>{v}</h2>
                            {displayBudgetValues(formValues[v], level + 1)}
                        </span>
                    );
                }
                if (v === 'Other, Describe') {
                    return (
                        <span key={'key' + index}>
                            <div className='row'>
                                <div
                                    className='columns small-12 large-4'
                                    style={incompleteModalStyles.dataHeader}
                                >
                                    {v}
                                </div>
                                <div
                                    className='columns small-12 large-8'
                                    style={incompleteModalStyles.dataField}
                                >
                                    {displayBudgetValues(formValues[v][0], level + 1)}
                                </div>
                            </div>
                            <div className='row'>
                                <div className='columns small-12 large-4'>&nbsp;</div>
                                <div
                                    className='columns small-12 large-8'
                                    style={incompleteModalStyles.dataField}
                                >
                                    {displayBudgetValues(formValues[v][1], level + 1)}
                                </div>
                            </div>
                        </span>
                    );
                } else {
                    return (
                        <div
                            key={'key' + index}
                            className='row'
                        >
                            <div
                                className='columns small-12 large-4'
                                style={incompleteModalStyles.dataHeader}
                            >
                                {v}
                            </div>
                            <div
                                className='columns small-12 large-8'
                                style={incompleteModalStyles.dataField}
                            >
                                {displayBudgetValues(formValues[v], level + 1)}
                            </div>
                        </div>
                    );
                }
            });
        } else {
            if (isNaN(formValues)) {
                return formValues;
            } else {
                return formatCurrency(formValues, 0);
            }
        }
    };

    const displaySpecSheetValues = (formValues, level) => {
        if (!formValues) {
            return '';
        }
        if (typeof formValues === 'object') {
            return Object.keys(formValues).map((v, index) => {
                if (level === 0) {
                    return (
                        <span key={'key' + index}>
                            <h2 className='decorated'>{v}</h2>
                            {displaySpecSheetValues(formValues[v], level + 1)}
                        </span>
                    );
                }
                return (
                    <div
                        key={'key' + index}
                        className='row'
                    >
                        <div
                            className='columns small-12 large-4'
                            style={incompleteModalStyles.dataHeader}
                        >
                            {v}
                        </div>
                        <div className='columns small-12 large-8'>
                            <textarea
                                rows={2}
                                disabled={true}
                                style={incompleteModalStyles.dataField}
                                value={displaySpecSheetValues(formValues[v], level + 1)}
                            />
                        </div>
                    </div>
                );
            });
        } else {
            return formValues;
        }
    };

    const displayFormData = (prop, index) => {
        const formsInfo = prop.formsInfo;
        if (!formsInfo || formsInfo.length === 0 || formsInfo <= 1) {
            return 'No Form Data entered';
        }
        index = index - 1;
        if (index > formsInfo.length - 1) {
            return;
        }
        const formValues = formsInfo[index].values;
        const isSpecSheet = formsInfo[index].type === 'Spec Sheet';
        if (isSpecSheet) {
            return displaySpecSheetValues(formValues, 0);
        } else {
            return displayBudgetValues(formValues, 0);
        }
    };

    const canShowBudgetTab = (prop) => {
        var form = prop.formsInfo.find((a) => a.type === 'Budget');
        return !!form;
    };

    const canShowSpecSheetTab = (prop) => {
        var form = prop.formsInfo.find((a) => a.type === 'Spec Sheet');
        return !!form;
    };

    const onRenderDateTime = (e) => {
        if (!e || !e.value) {
            return null;
        }
        return formatDateTime(e.value);
    };

    const onStatusRender = (e) => {
        const option = ApplicationStatusTypeOptions.find((o) => o.id === e.value);
        if (!option) {
            return 'Unknown';
        }
        return option.name;
    };

    if (!isOpen || !application) {
        return null;
    }

    return (
        <>
            <Popup
                aria-labelledby='form-dialog-title'
                visible={isOpen}
                onHiding={_handleClose}
                dragEnabled={false}
                hideOnOutsideClick={true}
                showCloseButton={false}
            >
                <div>
                    <div className='modal-container'>
                        <div className='modal-backdrop'></div>
                        <div
                            className='modal'
                            tabIndex='-1'
                            role='dialog'
                            style={{ width: '1000px' }}
                        >
                            <div
                                className='modal-dialog'
                                role='document'
                            >
                                <div>
                                    <div
                                        className='page-header'
                                        role='banner'
                                    >
                                        <h1>Incomplete Application Review</h1>
                                        <button
                                            type='button'
                                            className='close'
                                            data-dismiss='modal'
                                            aria-label='Close'
                                            onClick={_handleClose}
                                        >
                                            <span aria-hidden='true'>&times;</span>
                                        </button>
                                    </div>
                                    <ModalSummaryHeader application={application} />
                                    <form
                                        name='incompleteAppReviewForm'
                                        onSubmit={async (event) => await _submitChanges(event)}
                                    >
                                        <div
                                            className='scrollable-content'
                                            style={{ margin: '10px' }}
                                        >
                                            <div>
                                                <div className='row'>
                                                    <div
                                                        className='columns small-12 large-6'
                                                        style={incompleteModalStyles.noPaddingLeft}
                                                    >
                                                        <label htmlFor='responsibleId'>POC</label>
                                                        <select
                                                            id='responsibleId'
                                                            value={responsibleId || undefined}
                                                            onChange={(event) => set_responsibleId(event.target.value)}
                                                        >
                                                            <option
                                                                key=''
                                                                value=''
                                                            ></option>
                                                            {employees &&
                                                                employees.map((poc) => {
                                                                    return (
                                                                        <option
                                                                            key={poc.key}
                                                                            value={poc.key}
                                                                        >
                                                                            {poc.name}
                                                                        </option>
                                                                    );
                                                                })}
                                                        </select>
                                                    </div>
                                                </div>

                                                <Tabs
                                                    showNavButtons={true}
                                                    width={500}
                                                    selectedIndex={tabIndex}
                                                    onOptionChanged={onTabsSelectionChanged}
                                                >
                                                    <Item text='Summary' />
                                                    <Item text='Property Files & Forms' />
                                                    <Item text='Status History' />
                                                </Tabs>

                                                <div
                                                    style={{
                                                        border: '1px solid silver',
                                                        padding: '5px',
                                                        height: '500px',
                                                        overflow: 'auto',
                                                    }}
                                                >
                                                    {tabIndex === 0 && (
                                                        <div className='review'>
                                                            <section className='summary'>
                                                                <BorrowerReview
                                                                    printMode={true}
                                                                    borrower={application.information.borrower}
                                                                    collapsable={false}
                                                                />
                                                                <PropertiesReview
                                                                    printMode={true}
                                                                    properties={application.information.properties}
                                                                    collapsable={false}
                                                                    loanType={application.information.borrower.loanType}
                                                                />
                                                            </section>
                                                        </div>
                                                    )}
                                                    {tabIndex === 1 && (
                                                        <div>
                                                            {application.isPortfolio && (
                                                                <Tabs
                                                                    showNavButtons={true}
                                                                    width={500}
                                                                    selectedIndex={propertyTabIndex}
                                                                    onOptionChanged={onPropertyTabsSelectionChanged}
                                                                >
                                                                    {application.properties.map((p, index) => {
                                                                        return (
                                                                            <Item
                                                                                key={'propinfo' + index}
                                                                                text={'Prop ' + p.bfiNumberSuffix}
                                                                                disabled={p.disabled}
                                                                            />
                                                                        );
                                                                    })}
                                                                </Tabs>
                                                            )}
                                                            {property && (
                                                                <div
                                                                    style={{
                                                                        border: '1px solid silver',
                                                                        padding: '5px',
                                                                        marginBottom: '10px',
                                                                    }}
                                                                >
                                                                    <div>
                                                                        <div
                                                                            className='row'
                                                                            style={{
                                                                                marginBottom: '5px',
                                                                            }}
                                                                        >
                                                                            <div
                                                                                className='columns small-12 large-12'
                                                                                style={incompleteModalStyles.nonEditable}
                                                                            >
                                                                                {property.description && (
                                                                                    <div>
                                                                                        <b>
                                                                                            Property {property.bfiNumberSuffix} -{' '}
                                                                                            {property.description}
                                                                                        </b>
                                                                                    </div>
                                                                                )}
                                                                                {property.address && (
                                                                                    <div
                                                                                        style={{
                                                                                            marginTop: '5px',
                                                                                            marginBottom: '10px',
                                                                                            fontWeight: 'bold',
                                                                                        }}
                                                                                    >
                                                                                        {property.address?.street1}{' '}
                                                                                        {property.address?.street2}{' '}
                                                                                        {property.address?.city},{' '}
                                                                                        {property.address?.state}{' '}
                                                                                        {property.address?.zipCode}
                                                                                    </div>
                                                                                )}
                                                                            </div>
                                                                        </div>
                                                                        <Tabs
                                                                            showNavButtons={true}
                                                                            //width={500}
                                                                            selectedIndex={subTabIndex}
                                                                            onOptionChanged={onSubTabsSelectionChanged}
                                                                        >
                                                                            <Item text='Files' />
                                                                            {canShowBudgetTab(property) && (
                                                                                <Item text={'Budget Form'} />
                                                                            )}
                                                                            {canShowSpecSheetTab(property) && (
                                                                                <Item text={'SpecSheet Form'} />
                                                                            )}
                                                                        </Tabs>
                                                                        <div style={styles.formStyle}>
                                                                            {subTabIndex === 0 && (
                                                                                <div>
                                                                                    <FileList
                                                                                        parentId={property.id}
                                                                                        files={property.files}
                                                                                        onFileChange={onPropertyFileListChange}
                                                                                        disabled={property.disabled}
                                                                                    />
                                                                                </div>
                                                                            )}

                                                                            {subTabIndex > 0 && (
                                                                                <div>
                                                                                    {displayFormData(property, subTabIndex)}
                                                                                </div>
                                                                            )}
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                            )}
                                                            {!property && (
                                                                <div className='row'>
                                                                    <div
                                                                        className='columns small-12 large-12'
                                                                        style={incompleteModalStyles.noPaddingLeft}
                                                                    >
                                                                        None Defined
                                                                    </div>
                                                                </div>
                                                            )}
                                                        </div>
                                                    )}
                                                    {tabIndex === 2 && (
                                                        <div
                                                            style={{
                                                                padding: '0 10px 0 10px',
                                                            }}
                                                        >
                                                            <DataGrid
                                                                dataSource={auditEntries.statusChanges}
                                                                showBorders={true}
                                                                showRowLines={false}
                                                                rowAlternationEnabled={true}
                                                                remoteOperations={false}
                                                                allowColumnReordering={false}
                                                                allowColumnResizing={true}
                                                                cellHintEnabled={true}
                                                                noDataText='No Status history available'
                                                            >
                                                                <Column
                                                                    dataField='statusId'
                                                                    caption='Status'
                                                                    width={175}
                                                                    alignment={'left'}
                                                                    cellRender={onStatusRender}
                                                                />
                                                                <Column
                                                                    dataField='createdAt'
                                                                    caption='On'
                                                                    width={150}
                                                                    alignment={'left'}
                                                                    cellRender={onRenderDateTime}
                                                                />
                                                                <Column
                                                                    dataField='createdBy'
                                                                    caption='By'
                                                                    alignment={'left'}
                                                                />
                                                            </DataGrid>
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                        <div
                                            className='button-footer'
                                            style={incompleteModalStyles.buttonContainer}
                                        >
                                            <button
                                                style={incompleteModalStyles.buttonMarginRight}
                                                type='submit'
                                                className='button primary'
                                            >
                                                Save
                                            </button>
                                            <button
                                                type='button'
                                                className='button hollow'
                                                data-dismiss='modal'
                                                onClick={_handleClose}
                                            >
                                                Cancel
                                            </button>
                                        </div>
                                    </form>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </Popup>
            <LoadPanel
                hideOnOutsideClick={false}
                visible={loadingOpen}
                message={loadingMessage}
            />
        </>
    );
};

export default IncompleteReviewModal;
